import { useEffect, useRef, useState } from 'react';
import { api, connectIncident } from './api.js';
import { Handover } from './Handover.jsx';
import { Actions } from './Actions.jsx';
import { Objectives } from './Objectives.jsx';
import { SitePlan } from './SitePlan.jsx';
import { clockSeconds, clock, stamp } from './format.js';

/**
 * One incident, live.
 *
 * The board and the action tracker share this component rather than being two screens,
 * because they share one socket and one copy of the incident. Two components would mean
 * two sockets, two fetches and two versions of the truth on screen at the same time.
 */

const PRIORITIES = [
  { key: 1, label: 'P1' },
  { key: 2, label: 'P1–P2' },
  { key: 3, label: 'All' },
];

export function Incident({ incidentId, session, view, onStatus, onCounts }) {
  const [addingPlan, setAddingPlan] = useState(false);
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [handover, setHandover] = useState(null);
  const [maxPriority, setMaxPriority] = useState(3);
  const [hideDone, setHideDone] = useState(false);
  const disconnect = useRef(null);
  const countsAt = useRef(0);

  const mayTick = session.permissions.includes('checklist.tick');
  const mayNote = session.permissions.includes('log.write');
  const mayHandOver = session.permissions.includes('position.handover');
  const mayAction = session.permissions.includes('assignment.write');
  const mayEditChecklist = session.permissions.includes('checklist.edit');

  /**
   * Tell the shell its tallies are stale, at most once every few seconds.
   *
   * Without the throttle every tick from every person in the room triggers another list
   * fetch from every browser watching — on a busy incident that is the board attacking
   * its own server for the sake of a number in a header.
   */
  function countsChanged() {
    const now = Date.now();
    if (now - countsAt.current < 4000) return;
    countsAt.current = now;
    onCounts?.();
  }

  async function load() {
    try { setData(await api.incident(incidentId)); setError(null); }
    catch (err) { setError(err.code === 'not_found' ? 'That incident is not available.' : 'Could not load the incident.'); }
  }

  useEffect(() => {
    load();
    disconnect.current = connectIncident(incidentId, {
      onStatus: (s) => {
        onStatus(s);
        // Coming back from a drop, refetch rather than trusting what was on screen.
        // Anything that changed while the socket was down was never delivered.
        if (s === 'live') load();
      },
      onEvent: (msg) => {
        if (msg.type === 'item.changed') {
          setData((d) => d && {
            ...d,
            items: d.items.map((i) => (i.id === msg.item.id ? { ...i, ...msg.item } : i)),
            log: [msg.entry, ...d.log],
          });
          countsChanged();
        } else if (msg.type === 'action.changed') {
          setData((d) => {
            if (!d) return d;
            const known = d.actions.some((a) => a.id === msg.action.id);
            return {
              ...d,
              actions: known
                ? d.actions.map((a) => (a.id === msg.action.id ? msg.action : a))
                : [...d.actions, msg.action].sort((a, b) => a.seq - b.seq),
              log: [msg.entry, ...d.log],
            };
          });
          countsChanged();
        } else if (msg.type === 'incident.changed') {
          setData((d) => d && { ...d, incident: msg.incident, log: [msg.entry, ...d.log] });
          countsChanged();
        } else if (msg.type === 'siteplan.changed') {
          // The picture itself is refetched by the Site plan tab when it is open; what the
          // board needs is the line in the log, so the timeline is complete wherever you
          // happen to be standing.
          setData((d) => d && { ...d, log: msg.entry ? [msg.entry, ...d.log] : d.log });
        } else if (msg.type === 'log.entry') {
          setData((d) => d && { ...d, log: [msg.entry, ...d.log] });
        } else if (msg.type === 'position.handover') {
          // everyone watching sees the seat change hands, not just the two people involved
          setData((d) => d && {
            ...d,
            positions: d.positions.map((p) =>
              p.id === msg.positionId ? { ...p, lead_name: msg.holderName } : p),
            log: [msg.entry, ...d.log],
          });
        }
      },
    });
    return () => disconnect.current?.();
  }, [incidentId]);

  async function toggle(item) {
    const next = item.state === 'done' ? 'open' : 'done';
    // Optimistic, because a control room should not wait on a round trip to see a tick.
    // The socket echo is what makes it true; on failure it snaps back and says why.
    setData((d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? { ...i, state: next } : i)) }));
    try {
      await api.setItemState(incidentId, item.id, next);
    } catch (err) {
      setData((d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? { ...i, state: item.state } : i)) }));
      setError(err.status === 403 ? 'Your role cannot change that item.' : 'That change did not save.');
    }
  }

  async function submitNote(e) {
    e.preventDefault();
    if (!note.trim()) return;
    const text = note.trim();
    setNote('');
    try { await api.addNote(incidentId, text); }
    catch { setError('That entry did not save.'); setNote(text); }
  }

  if (error && !data) return <div className="card"><div className="empty">{error}</div></div>;
  if (!data) return <div className="card"><div className="empty">Loading…</div></div>;

  // Cumulative, not exclusive: "P1–P2" means everything at that priority or above, which
  // is how somebody under pressure reads it. A filter that hides P1s when you ask for P2
  // is a filter that gets somebody hurt.
  const visible = data.items.filter((i) =>
    i.priority <= maxPriority && !(hideDone && i.state === 'done'));

  const byPosition = data.positions.map((p) => ({
    ...p,
    items: visible.filter((i) => i.position_id === p.id),
    openP1: data.items.filter((i) => i.position_id === p.id && i.priority === 1 && i.state === 'open').length,
  }));

  const hiddenCount = data.items.length - visible.length;

  // Only the overarching ones on the board. A function's own objectives belong beside that
  // function's work, not in a list of what the incident is trying to achieve. Withdrawn
  // ones are left out entirely — they are kept on the record, and the record is the
  // Objectives tab, not the wall display.
  const incidentObjectives = (data.objectives ?? [])
    .filter((o) => o.position_id === null && o.state !== 'na');

  return (
    <>
      {view === 'siteplan'
        ? <SitePlan incidentId={incidentId} session={session} incident={data.incident} />
        : view === 'objectives'
        ? <Objectives incidentId={incidentId} session={session} positions={data.positions}
                      onChanged={load} />
        : view === 'actions'
        ? <Actions incidentId={incidentId} actions={data.actions} positions={data.positions}
                   strategies={data.strategies} canWrite={mayAction} onChanged={load} />
        : (
          <div className="card">
            <h2>
              {data.incident.ref} · {data.incident.name}
              <span className={`pill ${data.incident.mode}`}>{data.incident.mode}</span>

              <span className="tabs">
                {PRIORITIES.map((p) => (
                  <button key={p.key} className={`tab ${maxPriority === p.key ? 'on' : ''}`}
                          onClick={() => setMaxPriority(p.key)}>{p.label}</button>
                ))}
                <button className={`tab ${hideDone ? 'on' : ''}`} onClick={() => setHideDone((v) => !v)}>
                  Hide done
                </button>
              </span>

              {/* Layering a plan on part-way through. An incident that starts as a fire and
                  becomes a hazardous-materials incident needs that plan's actions THEN, and
                  until now plans could only be chosen at the moment the incident was opened.
                  Applying only ever adds, so the worst a mistaken click does is nothing. */}
              {mayEditChecklist && data.incident.status !== 'closed' && (
                <button className="hoBtn head" onClick={() => setAddingPlan(true)}>
                  Add a plan
                </button>
              )}
            </h2>

            {error && <p className="err inline">{error}</p>}

            {addingPlan && (
              <ApplyPlan incidentId={incidentId} onClose={() => setAddingPlan(false)}
                         onApplied={() => { setAddingPlan(false); load(); onCounts?.(); }} />
            )}

            {/* What is actually happening, above the checklists. It was being stored and
                never shown, so anybody arriving at the board — a relief shift, somebody
                joining remotely — had to work out the situation from the log. */}
            {data.incident.situation && (
              <p className="situation">{data.incident.situation}</p>
            )}

            {/* The overarching objectives, above the checklists — what the whole room is
                trying to achieve, before the detail of who is doing what. The counts beside
                each one are the action tracker, counted through the strategies underneath
                it, so nothing here is a second figure that somebody has to keep in step.
                Set on the Objectives tab; shown here because this is the screen that stays
                open on the wall. */}
            {incidentObjectives.length > 0 && (
              <div className="objPanel">
                <div className="objPanelHead">Incident objectives</div>
                <ul className="objSummary">
                  {incidentObjectives.map((o) => (
                    <li key={o.id} className={o.state}>
                      <span className="oSeq">O{o.seq}</span>
                      <span className="oTitle">{o.title}</span>
                      {o.state === 'done' && <span className="pill done">Achieved</span>}
                      <span className="spacer" />
                      <span className="rollup">
                        {o.tactics > 0
                          ? <>{o.tactics_done}/{o.tactics} tactics</>
                          : <span className="none">no tactics linked</span>}
                        {o.tactics_late > 0 && <span className="late"> · {o.tactics_late} LATE</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="body">
              {byPosition.length === 0 && <div className="empty">No positions on this incident yet.</div>}

              {byPosition.map((p) => (
                <div key={p.id}>
                  <div className="poshead">
                    <span>{p.name}</span>
                    {p.openP1 > 0 && <span className="tally bad small">{p.openP1} P1</span>}
                    <span className="held">
                      {p.lead_name ? p.lead_name : 'position not held'}
                      {p.held_since && ` · since ${clock(p.held_since)}`}
                    </span>
                    <button className="hoBtn" onClick={() => setHandover(p)}>
                      {p.lead_name ? 'Handover' : 'Assume'}
                    </button>
                  </div>

                  {p.items.length === 0 && (
                    <div className="empty listEmpty">
                      {data.items.some((i) => i.position_id === p.id)
                        ? 'Nothing at this priority.' : 'No actions.'}
                    </div>
                  )}

                  {p.items.map((item) => (
                    <label key={item.id} className={`item ${item.state === 'done' ? 'done' : ''}`}>
                      <input type="checkbox" checked={item.state === 'done'}
                             disabled={!mayTick} onChange={() => toggle(item)} />
                      <span className={`pri p${item.priority}`}>P{item.priority}</span>
                      <span className="txt">{item.text}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            {/* Said out loud, because a filtered board looks exactly like a short one. */}
            {hiddenCount > 0 && (
              <div className="filterNote">
                {hiddenCount} of {data.items.length} hidden by the filter
                <button className="linkbtn small" onClick={() => { setMaxPriority(3); setHideDone(false); }}>
                  show everything
                </button>
              </div>
            )}
          </div>
        )}

      <div className="card">
        <h2>Incident log</h2>
        <div className="body log">
          {data.log.length === 0 && <div className="empty">Nothing logged yet.</div>}
          {data.log.map((e) => (
            <div className="logrow" key={e.id}>
              <span className="when">{clockSeconds(e.at)}</span> <span className="who2">{e.actor_label}</span>
              <div className="msg">{e.message}</div>
            </div>
          ))}
        </div>
        {mayNote && (
          <form className="noteform" onSubmit={submitNote}>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an entry…" maxLength={2000} />
            <button type="submit">Log</button>
          </form>
        )}
      </div>

      {handover && (
        <Handover
          incidentId={incidentId}
          position={handover}
          canHandOver={mayHandOver}
          session={session}
          onDone={load}
          onClose={() => setHandover(null)}
        />
      )}
    </>
  );
}


/**
 * Choose an approved plan to layer onto a running incident.
 *
 * Only offers versions that are approved AND not already on this incident — a layer that
 * would add nothing is a button that does nothing, and somebody would press it twice and
 * conclude the feature is broken.
 */
function ApplyPlan({ incidentId, onClose, onApplied }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.incidentPlans(incidentId)
      .then(setState)
      .catch(() => setError('Could not load the plans.'));
  }, [incidentId]);

  async function apply(v) {
    setBusy(v.plan_version_id); setError(null);
    try {
      const out = await api.applyPlan(incidentId, v.plan_version_id);
      // Says what landed AND what did not. A plan naming a function this incident does not
      // run half-applies, and a silent partial application is worse than a refusal.
      setResult({ ...out, name: v.name, version: v.version });
    } catch (err) {
      setError(err.detail || 'That plan could not be applied.');
    } finally { setBusy(null); }
  }

  return (
    <div className="hoWrap" onClick={onClose}>
      <div className="ho" onClick={(e) => e.stopPropagation()}>
        <div className="hoHead">
          <div>
            <h3>Add a plan to this incident</h3>
            <p>Its actions are added to the functions they name. Nothing already on the
               board is changed or removed.</p>
          </div>
          <button className="hoX" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="hoBody">
          {error && <p className="err">{error}</p>}

          {result ? (
            <>
              <p className="applied">
                <b>{result.name} v{result.version}</b> applied — {result.added}{' '}
                action{result.added === 1 ? '' : 's'} added.
                {result.missingPositions?.length > 0 && (
                  <span className="warnNote">
                    {' '}This incident has no {result.missingPositions.join(', ')} function,
                    so those items were not added. Add the function and apply it again.
                  </span>
                )}
              </p>
              <div className="actions">
                <span className="spacer" />
                <button className="btn narrow" onClick={onApplied}>Done</button>
              </div>
            </>
          ) : !state ? (
            <div className="empty">Loading…</div>
          ) : (
            <>
              {state.applied.length > 0 && (
                <>
                  <h4 className="sectionHead">Already on this incident</h4>
                  <ul className="planPick done">
                    {state.applied.map((a) => (
                      <li key={a.plan_version_id}>
                        <span className="nm">{a.name}</span>
                        <span className="cd">v{a.version}</span>
                        <span className="spacer" />
                        <span className="when">{stamp(a.applied_at)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <h4 className="sectionHead">Available</h4>
              {state.available.length === 0 ? (
                <div className="empty">
                  Nothing left to add. Approved plans appear here — write one on the Plans
                  tab and approve it.
                </div>
              ) : (
                <ul className="planPick">
                  {state.available.map((v) => (
                    <li key={v.plan_version_id}>
                      <span className="nm">{v.name}</span>
                      <span className="cd">v{v.version}</span>
                      <span className="spacer" />
                      <span className="when">{v.items} item{v.items === 1 ? '' : 's'}</span>
                      <button className="hoBtn" disabled={busy !== null}
                              onClick={() => apply(v)}>
                        {busy === v.plan_version_id ? 'Adding…' : 'Add'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
